import * as Sentry from '@sentry/react';

/**
 * Observability for sheet load failures — PURE reporting, never
 * changes load behaviour. Deliberately carries no cell contents, file name or
 * presigned URL: `ext` (extension only) is the only file-identifying field,
 * and `message` is truncated.
 * Pattern: PlateEditor/exporters/paginationTelemetry.ts.
 */

const MAX_MESSAGE_LENGTH = 200;

export type SheetLoadStage = 'download' | 'parse' | 'too_large' | 'worker_boot' | 'read_only';
export type SheetLoadContext = 'editor' | 'view' | 'preview' | 'import';

export interface SheetLoadFailureArgs {
    stage: SheetLoadStage;
    context: SheetLoadContext;
    fileBytes: number;
    sheetCount?: number;
    cellCount?: number;
    elapsedMs: number;
    message: string;
    ext: string;
}

export function logSheetLoadFailure(args: SheetLoadFailureArgs): void {
    try {
        const data = {
            stage: args.stage,
            context: args.context,
            ext: args.ext,
            fileBytes: args.fileBytes,
            sheetCount: args.sheetCount,
            cellCount: args.cellCount,
            elapsedMs: args.elapsedMs,
            message: args.message.slice(0, MAX_MESSAGE_LENGTH),
        };
        if (args.stage === 'read_only') {
            // Informational: a file opened read-only is not a load failure.
            // eslint-disable-next-line no-console
            console.info('[SheetEditor] opened read-only', data);
            Sentry.addBreadcrumb({ category: 'sheet-editor.load', level: 'info', message: `sheet-load: ${args.stage}`, data });
            return;
        }
        // eslint-disable-next-line no-console
        console.error('[SheetEditor] load failed', data);
        Sentry.addBreadcrumb({ category: 'sheet-editor.load', level: 'error', message: `sheet-load: ${args.stage}`, data });
        if (args.stage === 'parse') {
            Sentry.captureException(new Error(data.message));
        } else if (args.stage === 'too_large') {
            Sentry.captureMessage('sheet-load: too_large', 'info');
        }
    } catch {
        // Telemetry must never affect the editor.
    }
}
